import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import { EXCLUDE_REFERENTI } from '../utils/contactPollution';
import { toLocalYmd } from '../utils/localYmd';
import type {
  MarketingOverview,
  CampaignPerformance,
  DailyTrendPoint,
  SourceComparison,
  CampaignDetail,
  AdCampaign,
  AdDailyStat,
  MarketingDatePreset,
  AggregatedStats,
  AdsetWithStats,
  AdWithStats,
  LeadQualityMetrics,
  PeriodComparisonItem,
} from '../types/marketing';

function getDatePresetRange(preset: MarketingDatePreset): { from: Date; to: Date } {
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case '7d': {
      const from = new Date(to);
      from.setDate(from.getDate() - 7);
      return { from, to };
    }
    case '30d': {
      const from = new Date(to);
      from.setDate(from.getDate() - 30);
      return { from, to };
    }
    case '90d': {
      const from = new Date(to);
      from.setDate(from.getDate() - 90);
      return { from, to };
    }
    case 'ytd': {
      const from = new Date(to.getFullYear(), 0, 1);
      return { from, to };
    }
    case 'this_month': {
      const from = new Date(to.getFullYear(), to.getMonth(), 1);
      return { from, to };
    }
    case 'last_month': {
      const from = new Date(to.getFullYear(), to.getMonth() - 1, 1);
      const toEnd = new Date(to.getFullYear(), to.getMonth(), 0);
      return { from, to: toEnd };
    }
    default:
      return { from: new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000), to };
  }
}

// Data locale, non UTC: con toISOString() in Italia "oggi" diventava "ieri" (segnalazione 00ac5d1b).
function formatDate(d: Date): string {
  return toLocalYmd(d);
}

interface MarketingStore {
  // Data
  overview: MarketingOverview[];
  /** Overview del periodo IMMEDIATAMENTE precedente (stessa durata) per i delta KPI. */
  overviewPrevious: MarketingOverview[];
  campaignPerformance: CampaignPerformance[];
  trendData: DailyTrendPoint[];
  sourceComparison: SourceComparison[];
  campaigns: AdCampaign[];

  // Filters
  dateRange: { from: Date; to: Date };
  datePreset: MarketingDatePreset;
  platform: 'all' | 'meta' | 'google' | 'linkedin';
  activeOnly: boolean;

  // Loading states
  overviewLoading: boolean;
  campaignsLoading: boolean;
  trendLoading: boolean;
  sourceLoading: boolean;
  error: string | null;

  // Actions
  setDatePreset: (preset: MarketingDatePreset) => void;
  setDateRange: (from: Date, to: Date) => void;
  setPlatform: (platform: 'all' | 'meta' | 'google' | 'linkedin') => void;
  setActiveOnly: (active: boolean) => void;
  /** Hydrate filtri da URL al mount senza triggerare refetch (chi chiama si occupa di fetchAll). */
  hydrateFiltersFromUrl: (preset: MarketingDatePreset | null, platform: 'all' | 'meta' | 'google' | 'linkedin' | null, activeOnly: boolean, customRange?: { from: Date; to: Date } | null) => void;
  fetchOverview: () => Promise<void>;
  fetchOverviewPrevious: () => Promise<void>;
  fetchCampaignPerformance: () => Promise<void>;
  fetchTrendData: () => Promise<void>;
  fetchSourceComparison: () => Promise<void>;
  fetchCampaigns: () => Promise<void>;
  fetchCampaignDetail: (campaignId: string) => Promise<CampaignDetail | null>;
  fetchLeadQualityMetrics: (campaignExternalId: string) => Promise<LeadQualityMetrics | null>;
  fetchPeriodComparison: (campaignExternalId: string, p1From: Date, p1To: Date, p2From: Date, p2To: Date) => Promise<PeriodComparisonItem[]>;
  fetchPlacementBreakdown: (campaignExternalId: string) => Promise<Array<{ placement: string; spend: number; impressions: number; clicks: number; reach: number }>>;
  fetchAll: () => Promise<void>;
}

export const useMarketingStore = create<MarketingStore>((set, get) => ({
  overview: [],
  overviewPrevious: [],
  campaignPerformance: [],
  trendData: [],
  sourceComparison: [],
  campaigns: [],
  dateRange: getDatePresetRange('30d'),
  datePreset: '30d',
  platform: 'all',
  activeOnly: false,
  overviewLoading: false,
  campaignsLoading: false,
  trendLoading: false,
  sourceLoading: false,
  error: null,

  setDatePreset: (preset: MarketingDatePreset) => {
    const range = getDatePresetRange(preset);
    set({ datePreset: preset, dateRange: range });
    get().fetchAll();
  },

  setDateRange: (from: Date, to: Date) => {
    set({ dateRange: { from, to }, datePreset: 'custom' });
    get().fetchAll();
  },

  setPlatform: (platform: 'all' | 'meta' | 'google' | 'linkedin') => {
    set({ platform });
    get().fetchAll();
  },

  // Filtro client-side su campaignPerformance già caricato: nessun refetch necessario.
  setActiveOnly: (active: boolean) => set({ activeOnly: active }),

  // Set raw senza refetch: usato dal hook URL→store al mount per evitare
  // 2-3 fetchAll() concorrenti (un fetchAll lo fa AdsPage una volta sola).
  hydrateFiltersFromUrl: (preset, platform, activeOnly, customRange) => {
    const patch: Partial<MarketingStore> = { activeOnly };
    if (preset === 'custom' && customRange) {
      patch.datePreset = 'custom';
      patch.dateRange = customRange;
    } else if (preset) {
      patch.datePreset = preset;
      patch.dateRange = getDatePresetRange(preset);
    }
    if (platform) {
      patch.platform = platform;
    }
    set(patch);
  },

  fetchOverview: async () => {
    set({ overviewLoading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange, platform } = get();

      const { data, error } = await supabase.rpc('get_marketing_overview', {
        p_organization_id: organizationId,
        p_date_from: formatDate(dateRange.from),
        p_date_to: formatDate(dateRange.to),
        p_platform: platform === 'all' ? null : platform,
      });

      if (error) throw error;
      set({ overview: data || [], overviewLoading: false });
    } catch (error: any) {
      console.error('Error fetching marketing overview:', error);
      set({ error: friendlyDbError(error), overviewLoading: false });
    }
  },

  // Periodo precedente di pari durata, che termina il giorno prima di `from`.
  // Serve ai delta ▲▼ delle KPI card (confronto periodo-su-periodo stile HubSpot).
  fetchOverviewPrevious: async () => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange, platform } = get();
      const lenMs = dateRange.to.getTime() - dateRange.from.getTime();
      const prevTo = new Date(dateRange.from.getTime() - 24 * 60 * 60 * 1000);
      const prevFrom = new Date(prevTo.getTime() - lenMs);

      const { data, error } = await supabase.rpc('get_marketing_overview', {
        p_organization_id: organizationId,
        p_date_from: formatDate(prevFrom),
        p_date_to: formatDate(prevTo),
        p_platform: platform === 'all' ? null : platform,
      });
      if (error) throw error;
      set({ overviewPrevious: data || [] });
    } catch (error: any) {
      console.error('Error fetching previous-period overview:', error);
      set({ overviewPrevious: [] });
    }
  },

  fetchCampaignPerformance: async () => {
    set({ campaignsLoading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange, platform } = get();

      const { data, error } = await supabase.rpc('get_campaign_performance', {
        p_organization_id: organizationId,
        p_date_from: formatDate(dateRange.from),
        p_date_to: formatDate(dateRange.to),
        p_platform: platform === 'all' ? null : platform,
      });

      if (error) throw error;
      set({ campaignPerformance: data || [], campaignsLoading: false });
    } catch (error: any) {
      console.error('Error fetching campaign performance:', error);
      set({ error: friendlyDbError(error), campaignsLoading: false });
    }
  },

  fetchTrendData: async () => {
    set({ trendLoading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange, platform } = get();

      let query = supabase
        .from('ad_daily_stats')
        .select('stat_date, spend, impressions, clicks, leads')
        .eq('organization_id', organizationId)
        .eq('entity_type', 'campaign')
        .gte('stat_date', formatDate(dateRange.from))
        .lte('stat_date', formatDate(dateRange.to))
        .order('stat_date', { ascending: true });

      if (platform !== 'all') {
        query = query.eq('platform', platform);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Aggregate by date
      const byDate: Record<string, DailyTrendPoint> = {};
      for (const row of data || []) {
        const key = row.stat_date;
        if (!byDate[key]) {
          byDate[key] = { date: key, spend: 0, impressions: 0, clicks: 0, leads: 0, revenue: 0, paid_revenue: 0 };
        }
        byDate[key].spend += Number(row.spend) || 0;
        byDate[key].impressions += row.impressions || 0;
        byDate[key].clicks += row.clicks || 0;
        byDate[key].leads += row.leads || 0;
      }

      set({ trendData: Object.values(byDate), trendLoading: false });
    } catch (error: any) {
      console.error('Error fetching trend data:', error);
      set({ error: friendlyDbError(error), trendLoading: false });
    }
  },

  fetchSourceComparison: async () => {
    set({ sourceLoading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange } = get();

      // Query clients con attribuzione, raggruppati per lead_source + ad_platform
      const { data: clients, error } = await supabase
        .from('clients')
        .select(`
          id, lead_source, ad_platform, ad_campaign_id,
          deals(id, status, amount, fattureincloud_invoices(paid_amount)),
          lead_source_data:lead_sources(name)
        `)
        .eq('organization_id', organizationId)
        .or(EXCLUDE_REFERENTI) // no persone-referente nel confronto sorgenti (leads_count gonfiato)
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());

      if (error) throw error;

      const sourceMap: Record<string, SourceComparison> = {};
      for (const client of clients || []) {
        const sourceName = client.ad_platform
          ? `${client.ad_platform} Ads`
          : ((client as any).lead_source_data?.name || 'Altro');
        const key = `${sourceName}_${client.ad_platform || 'organic'}`;

        if (!sourceMap[key]) {
          sourceMap[key] = {
            source: sourceName,
            platform: client.ad_platform || undefined,
            leads_count: 0,
            deals_count: 0,
            deals_won_count: 0,
            revenue: 0,
            paid_revenue: 0,
            avg_deal_value: 0,
            conversion_rate: 0,
          };
        }

        sourceMap[key].leads_count++;
        const deals = (client as any).deals || [];
        sourceMap[key].deals_count += deals.length;
        const wonDeals = deals.filter((d: any) => d.status === 'accepted');
        sourceMap[key].deals_won_count += wonDeals.length;
        sourceMap[key].revenue += wonDeals.reduce((sum: number, d: any) => sum + (Number(d.amount) || 0), 0);
        // Aggregate paid_revenue from fattureincloud_invoices
        for (const deal of deals) {
          const invoices = deal.fattureincloud_invoices || [];
          sourceMap[key].paid_revenue += invoices.reduce((sum: number, inv: any) => sum + (Number(inv.paid_amount) || 0), 0);
        }
      }

      // Calcola metriche derivate
      const comparisons = Object.values(sourceMap).map(s => ({
        ...s,
        avg_deal_value: s.deals_won_count > 0 ? Math.round(s.revenue / s.deals_won_count) : 0,
        conversion_rate: s.leads_count > 0 ? Math.round((s.deals_won_count / s.leads_count) * 100 * 100) / 100 : 0,
      }));

      set({ sourceComparison: comparisons, sourceLoading: false });
    } catch (error: any) {
      console.error('Error fetching source comparison:', error);
      set({ error: friendlyDbError(error), sourceLoading: false });
    }
  },

  fetchCampaigns: async () => {
    set({ campaignsLoading: true });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { platform } = get();

      let query = supabase
        .from('ad_campaigns')
        .select('*')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false });

      if (platform !== 'all') {
        query = query.eq('platform', platform);
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ campaigns: data || [], campaignsLoading: false });
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      set({ campaignsLoading: false });
    }
  },

  fetchCampaignDetail: async (campaignId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange } = get();

      // Fetch campaign
      const { data: campaign, error: campaignError } = await supabase
        .from('ad_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (campaignError) throw campaignError;
      if (!campaign) return null;

      // Fetch adsets
      const { data: adsets } = await supabase
        .from('ad_adsets')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('name');

      // Fetch ads
      const adsetIds = (adsets || []).map(a => a.id);
      const { data: ads } = adsetIds.length > 0
        ? await supabase.from('ad_ads').select('*').in('adset_id', adsetIds).order('name')
        : { data: [] };

      // Fetch campaign-level stats
      const { data: stats } = await supabase
        .from('ad_daily_stats')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('external_entity_id', campaign.external_id)
        .eq('entity_type', 'campaign')
        .gte('stat_date', formatDate(dateRange.from))
        .lte('stat_date', formatDate(dateRange.to))
        .order('stat_date', { ascending: true });

      // Fetch adset-level stats
      const adsetExternalIds = (adsets || []).map(a => a.external_id);
      const { data: adsetStats } = adsetExternalIds.length > 0
        ? await supabase
            .from('ad_daily_stats')
            .select('*')
            .eq('organization_id', organizationId)
            .eq('entity_type', 'adset')
            .in('external_entity_id', adsetExternalIds)
            .gte('stat_date', formatDate(dateRange.from))
            .lte('stat_date', formatDate(dateRange.to))
        : { data: [] };

      // Fetch ad-level stats
      const adExternalIds = (ads || []).map(a => a.external_id);
      const { data: adStats } = adExternalIds.length > 0
        ? await supabase
            .from('ad_daily_stats')
            .select('*')
            .eq('organization_id', organizationId)
            .eq('entity_type', 'ad')
            .in('external_entity_id', adExternalIds)
            .gte('stat_date', formatDate(dateRange.from))
            .lte('stat_date', formatDate(dateRange.to))
        : { data: [] };

      // Fetch attributed clients (with adset/ad-level attribution)
      const { data: attributedClients } = await supabase
        .from('clients')
        .select('id, business_name, email, created_at, ad_attribution_method, ad_adset_id, ad_ad_id, deals(id, status, amount)')
        .eq('organization_id', organizationId)
        .eq('ad_campaign_id', campaign.external_id)
        .order('created_at', { ascending: false })
        .limit(200);

      // Helper: aggregate stats array into single object
      const aggregateStats = (rows: AdDailyStat[], crmClients: any[]): AggregatedStats => {
        const spend = rows.reduce((s, r) => s + (Number(r.spend) || 0), 0);
        const impressions = rows.reduce((s, r) => s + (r.impressions || 0), 0);
        const clicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
        const reach = rows.reduce((s, r) => s + (r.reach || 0), 0);
        const link_clicks = rows.reduce((s, r) => s + (r.link_clicks || 0), 0);
        const leads = rows.reduce((s, r) => s + (r.leads || 0), 0);
        const video_views = rows.reduce((s, r) => s + (r.video_views || 0), 0);
        const wonDeals = crmClients.flatMap((c: any) => (c.deals || []).filter((d: any) => d.status === 'accepted'));
        return {
          spend,
          impressions,
          reach,
          clicks,
          link_clicks,
          ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
          cpc: clicks > 0 ? spend / clicks : 0,
          cpm: impressions > 0 ? (spend / impressions) * 1000 : 0,
          leads,
          cost_per_lead: leads > 0 ? spend / leads : 0,
          video_views,
          crm_leads: crmClients.length,
          crm_deals_won: wonDeals.length,
          crm_revenue: wonDeals.reduce((s: number, d: any) => s + (Number(d.amount) || 0), 0),
        };
      };

      // Group stats by external_entity_id
      const groupByEntity = (rows: any[]) => {
        const map: Record<string, any[]> = {};
        for (const r of rows || []) {
          const key = r.external_entity_id;
          if (!map[key]) map[key] = [];
          map[key].push(r);
        }
        return map;
      };

      const adsetStatsMap = groupByEntity(adsetStats || []);
      const adStatsMap = groupByEntity(adStats || []);

      // Group clients by adset/ad for CRM metrics at sub-levels
      const clientsByAdset: Record<string, any[]> = {};
      const clientsByAd: Record<string, any[]> = {};
      for (const c of attributedClients || []) {
        if (c.ad_adset_id) {
          if (!clientsByAdset[c.ad_adset_id]) clientsByAdset[c.ad_adset_id] = [];
          clientsByAdset[c.ad_adset_id].push(c);
        }
        if (c.ad_ad_id) {
          if (!clientsByAd[c.ad_ad_id]) clientsByAd[c.ad_ad_id] = [];
          clientsByAd[c.ad_ad_id].push(c);
        }
      }

      // Build ads with stats
      const adsWithStats: AdWithStats[] = (ads || []).map(ad => ({
        ...ad,
        aggregated_stats: aggregateStats(
          adStatsMap[ad.external_id] || [],
          clientsByAd[ad.external_id] || []
        ),
      }));

      // Build adsets with stats and nested ads
      const adsetsWithStats: AdsetWithStats[] = (adsets || []).map(adset => ({
        ...adset,
        aggregated_stats: aggregateStats(
          adsetStatsMap[adset.external_id] || [],
          clientsByAdset[adset.external_id] || []
        ),
        ads_with_stats: adsWithStats.filter(a => a.adset_id === adset.id),
      }));

      // Build trend from campaign stats
      const trend: DailyTrendPoint[] = (stats || []).map((s: AdDailyStat) => ({
        date: s.stat_date,
        spend: Number(s.spend) || 0,
        impressions: s.impressions || 0,
        clicks: s.clicks || 0,
        leads: s.leads || 0,
        revenue: 0,
        paid_revenue: 0,
      }));

      const clientsFormatted = (attributedClients || []).map((c: any) => ({
        id: c.id,
        business_name: c.business_name,
        email: c.email,
        created_at: c.created_at,
        ad_attribution_method: c.ad_attribution_method,
        deals_count: c.deals?.length || 0,
        revenue: c.deals?.filter((d: any) => d.status === 'accepted').reduce((sum: number, d: any) => sum + (Number(d.amount) || 0), 0) || 0,
      }));

      return {
        campaign,
        adsets: adsetsWithStats,
        ads: adsWithStats,
        stats: stats || [],
        trend,
        attributed_clients: clientsFormatted,
      };
    } catch (error: any) {
      console.error('Error fetching campaign detail:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  fetchLeadQualityMetrics: async (campaignExternalId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();

      const { data: clients, error } = await supabase
        .from('clients')
        .select(`
          id, created_at,
          deals(id, status, amount, updated_at),
          activities:activities(id)
        `)
        .eq('organization_id', organizationId)
        .eq('ad_campaign_id', campaignExternalId);

      if (error) throw error;
      if (!clients || clients.length === 0) return null;

      const totalLeads = clients.length;
      const wonDeals = clients.flatMap((c: any) => (c.deals || []).filter((d: any) => d.status === 'accepted'));
      const dealsWon = wonDeals.length;
      const totalRevenue = wonDeals.reduce((s: number, d: any) => s + (Number(d.amount) || 0), 0);

      // Avg close time: from client creation to deal won (updated_at)
      let totalCloseDays = 0;
      let closeCount = 0;
      for (const c of clients as any[]) {
        for (const d of (c.deals || [])) {
          if (d.status === 'accepted' && d.updated_at) {
            const created = new Date(c.created_at).getTime();
            const closed = new Date(d.updated_at).getTime();
            totalCloseDays += (closed - created) / (1000 * 60 * 60 * 24);
            closeCount++;
          }
        }
      }

      // Unworked: no deals AND no activities
      const unworked = clients.filter((c: any) =>
        (!c.deals || c.deals.length === 0) && (!c.activities || c.activities.length === 0)
      ).length;

      return {
        conversion_rate: totalLeads > 0 ? (dealsWon / totalLeads) * 100 : 0,
        avg_deal_value: dealsWon > 0 ? totalRevenue / dealsWon : 0,
        avg_close_time_days: closeCount > 0 ? totalCloseDays / closeCount : 0,
        unworked_pct: totalLeads > 0 ? (unworked / totalLeads) * 100 : 0,
        total_leads: totalLeads,
        deals_won: dealsWon,
        total_revenue: totalRevenue,
      };
    } catch (error: any) {
      console.error('Error fetching lead quality metrics:', error);
      return null;
    }
  },

  fetchPeriodComparison: async (campaignExternalId: string, p1From: Date, p1To: Date, p2From: Date, p2To: Date) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();

      const fetchPeriod = async (from: Date, to: Date) => {
        const { data } = await supabase
          .from('ad_daily_stats')
          .select('spend, impressions, clicks, reach, leads, cost_per_lead, ctr, cpc, cpm')
          .eq('organization_id', organizationId)
          .eq('entity_type', 'campaign')
          .eq('external_entity_id', campaignExternalId)
          .gte('stat_date', formatDate(from))
          .lte('stat_date', formatDate(to));

        const rows = data || [];
        return {
          spend: rows.reduce((s, r) => s + (Number(r.spend) || 0), 0),
          impressions: rows.reduce((s, r) => s + (r.impressions || 0), 0),
          clicks: rows.reduce((s, r) => s + (r.clicks || 0), 0),
          reach: rows.reduce((s, r) => s + (r.reach || 0), 0),
          leads: rows.reduce((s, r) => s + (r.leads || 0), 0),
          cpl: rows.reduce((s, r) => s + (Number(r.cost_per_lead) || 0), 0) / (rows.length || 1),
          ctr: rows.reduce((s, r) => s + (Number(r.ctr) || 0), 0) / (rows.length || 1),
          cpc: rows.reduce((s, r) => s + (Number(r.cpc) || 0), 0) / (rows.length || 1),
          cpm: rows.reduce((s, r) => s + (Number(r.cpm) || 0), 0) / (rows.length || 1),
        };
      };

      const [d1, d2] = await Promise.all([fetchPeriod(p1From, p1To), fetchPeriod(p2From, p2To)]);

      const delta = (v1: number, v2: number) => v1 !== 0 ? ((v2 - v1) / v1) * 100 : (v2 > 0 ? 100 : 0);

      const items: PeriodComparisonItem[] = [
        { metric: 'spend', label: 'Spesa', value_p1: d1.spend, value_p2: d2.spend, delta_pct: delta(d1.spend, d2.spend), format: 'currency' },
        { metric: 'impressions', label: 'Impressioni', value_p1: d1.impressions, value_p2: d2.impressions, delta_pct: delta(d1.impressions, d2.impressions), format: 'number' },
        { metric: 'clicks', label: 'Click', value_p1: d1.clicks, value_p2: d2.clicks, delta_pct: delta(d1.clicks, d2.clicks), format: 'number' },
        { metric: 'reach', label: 'Copertura', value_p1: d1.reach, value_p2: d2.reach, delta_pct: delta(d1.reach, d2.reach), format: 'number' },
        { metric: 'ctr', label: 'CTR', value_p1: d1.ctr, value_p2: d2.ctr, delta_pct: delta(d1.ctr, d2.ctr), format: 'percent' },
        { metric: 'cpc', label: 'CPC', value_p1: d1.cpc, value_p2: d2.cpc, delta_pct: delta(d1.cpc, d2.cpc), format: 'currency' },
        { metric: 'cpm', label: 'CPM', value_p1: d1.cpm, value_p2: d2.cpm, delta_pct: delta(d1.cpm, d2.cpm), format: 'currency' },
        { metric: 'leads', label: 'Lead', value_p1: d1.leads, value_p2: d2.leads, delta_pct: delta(d1.leads, d2.leads), format: 'number' },
        { metric: 'cpl', label: 'Costo per Lead', value_p1: d1.cpl, value_p2: d2.cpl, delta_pct: delta(d1.cpl, d2.cpl), format: 'currency' },
      ];

      return items;
    } catch (error: any) {
      console.error('Error fetching period comparison:', error);
      return [];
    }
  },

  fetchPlacementBreakdown: async (campaignExternalId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { dateRange } = get();

      const { data, error } = await supabase
        .from('ad_placement_stats')
        .select('placement, spend, impressions, clicks, reach, ctr, cpc, cpm')
        .eq('organization_id', organizationId)
        .eq('entity_type', 'campaign')
        .eq('external_entity_id', campaignExternalId)
        .gte('stat_date', formatDate(dateRange.from))
        .lte('stat_date', formatDate(dateRange.to));

      if (error) throw error;

      // Aggregate by placement
      const map: Record<string, { placement: string; spend: number; impressions: number; clicks: number; reach: number }> = {};
      for (const row of data || []) {
        const key = row.placement;
        if (!map[key]) map[key] = { placement: key, spend: 0, impressions: 0, clicks: 0, reach: 0 };
        map[key].spend += Number(row.spend) || 0;
        map[key].impressions += row.impressions || 0;
        map[key].clicks += row.clicks || 0;
        map[key].reach += row.reach || 0;
      }

      return Object.values(map).sort((a, b) => b.spend - a.spend);
    } catch (error: any) {
      console.error('Error fetching placement breakdown:', error);
      return [];
    }
  },

  fetchAll: async () => {
    const store = get();
    await Promise.all([
      store.fetchOverview(),
      store.fetchOverviewPrevious(),
      store.fetchCampaignPerformance(),
      store.fetchTrendData(),
      store.fetchSourceComparison(),
      // campaigns serve per il cross-reference platform_data._ad_account_id
      // → filtro client-side per account selezionato nel cockpit ADS.
      store.fetchCampaigns(),
    ]);
  },
}));
